import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Pause, Volume2, VolumeX, CheckCircle2,
  AlertTriangle, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';

export const CourseSlideContent = ({
  currentSlide, slides, imageUrl, audioUrl, audioLoading,
  audioPlaying, audioMuted, audioRef,
  quizAnswers, quizResults, quizSubmitting, quizScoreFromProgress, quizSlideId,
  exerciseAnswer, exerciseRevealed,
  onToggleAudio, onToggleMute, onAudioEnded,
  onSetQuizAnswers, onSubmitQuiz,
  onSetExerciseAnswer, onRevealExercise
}) => {
  const currentSlideData = slides[currentSlide];
  if (!currentSlideData) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentSlide}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
        data-testid="slide-content"
      >
        {/* Slide header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider mr-2 ${
              currentSlideData.type === 'quiz' ? 'bg-amber-100 text-amber-700' :
              currentSlideData.type === 'exercise' ? 'bg-purple-100 text-purple-700' :
              'bg-teal-100 text-teal-700'
            }`}>
              {currentSlideData.type === 'quiz' ? 'Quiz' : currentSlideData.type === 'exercise' ? 'Exercice' : 'Lecon'}
            </span>
            <h2 className="text-lg font-bold text-slate-900 mt-1">{currentSlideData.title}</h2>
          </div>
          <span className="text-sm text-slate-400">{currentSlide + 1} / {slides.length}</span>
        </div>

        {/* Image */}
        {imageUrl && (
          <div className="px-6 pt-4">
            <img
              src={imageUrl}
              alt={currentSlideData.title}
              className="w-full max-h-72 object-contain rounded-lg bg-slate-50"
              data-testid="slide-image"
              onError={(e) => e.target.style.display = 'none'}
            />
          </div>
        )}

        {/* Content */}
        <div className="px-6 py-4">
          {currentSlideData.type === 'lesson' && (
            <div
              className="prose prose-slate max-w-none text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: currentSlideData.content }}
              data-testid="lesson-content"
            />
          )}

          {currentSlideData.type === 'quiz' && (
            <div data-testid="quiz-section">
              <div className="prose prose-slate max-w-none text-sm mb-4" dangerouslySetInnerHTML={{ __html: currentSlideData.content }} />

              {!quizResults && quizScoreFromProgress && currentSlideData.id === quizSlideId && (
                <div className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${quizScoreFromProgress.passed ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="quiz-previous-result">
                  {quizScoreFromProgress.passed
                    ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                    : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />}
                  <div>
                    <p className={`font-semibold text-sm ${quizScoreFromProgress.passed ? 'text-green-800' : 'text-amber-800'}`}>
                      Score precedent : {quizScoreFromProgress.score}%
                    </p>
                    <p className="text-xs text-slate-600">
                      {quizScoreFromProgress.passed ? 'Quiz reussi ! Vous pouvez passer à la suite.' : 'Score insuffisant. Vous pouvez retenter en soumettant a nouveau.'}
                    </p>
                  </div>
                </div>
              )}

              {(currentSlideData.quiz_data?.questions || []).map((q, qi) => (
                <div key={qi} className="mb-6 p-4 bg-slate-50 rounded-lg" data-testid={`quiz-question-${qi}`}>
                  <p className="font-semibold text-slate-900 mb-3 text-sm">{qi + 1}. {q.question}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => {
                      const isSelected = quizAnswers[qi] === oi;
                      const hasResult = quizResults?.results?.[qi];
                      const isCorrect = hasResult && oi === q.correct_index;
                      const isWrong = hasResult && isSelected && !isCorrect;
                      return (
                        <button
                          key={oi}
                          onClick={() => !quizResults && onSetQuizAnswers({ ...quizAnswers, [qi]: oi })}
                          disabled={!!quizResults}
                          className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all ${
                            isCorrect ? 'bg-green-50 border-green-400 text-green-800' :
                            isWrong ? 'bg-red-50 border-red-400 text-red-800' :
                            isSelected ? 'bg-teal-50 border-teal-400 text-teal-800' :
                            'bg-white border-slate-200 hover:border-teal-300'
                          }`}
                          data-testid={`quiz-option-${qi}-${oi}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {quizResults?.results?.[qi] && (
                    <p className={`mt-2 text-xs ${quizResults.results[qi].is_correct ? 'text-green-600' : 'text-red-600'}`}>
                      {quizResults.results[qi].explanation}
                    </p>
                  )}
                </div>
              ))}
              {!quizResults ? (
                <Button onClick={onSubmitQuiz} disabled={quizSubmitting || Object.keys(quizAnswers).length === 0} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-submit-quiz">
                  {quizSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Valider mes réponses
                </Button>
              ) : (
                <div className={`p-4 rounded-lg text-center ${quizResults.score >= 70 ? 'bg-green-50' : 'bg-red-50'}`} data-testid="quiz-result">
                  <p className="font-bold text-lg">{quizResults.score}%</p>
                  <p className="text-sm">{quizResults.correct}/{quizResults.total} réponses correctes</p>
                </div>
              )}
            </div>
          )}

          {currentSlideData.type === 'exercise' && (
            <div data-testid="exercise-section">
              <div className="prose prose-slate max-w-none text-sm mb-4" dangerouslySetInnerHTML={{ __html: currentSlideData.content }} />
              {currentSlideData.exercise_data && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2 text-sm">Instructions</h4>
                  <p className="text-sm text-slate-700 mb-4">{currentSlideData.exercise_data.instructions}</p>
                  {currentSlideData.exercise_data.hints?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Indices</p>
                      <ul className="list-disc pl-4 text-sm text-slate-600 space-y-1">
                        {currentSlideData.exercise_data.hints.map((h, i) => <li key={i}>{h}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Votre réponse :</label>
                    <textarea
                      value={exerciseAnswer}
                      onChange={(e) => onSetExerciseAnswer(e.target.value)}
                      rows={4}
                      placeholder="Redigez votre réponse ici avant de pouvoir consulter le corrige..."
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm resize-y focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
                      disabled={exerciseRevealed}
                      data-testid="exercise-answer-input"
                    />
                  </div>
                  {!exerciseRevealed ? (
                    <Button
                      onClick={onRevealExercise}
                      disabled={exerciseAnswer.trim().length < 10}
                      variant="outline"
                      className="border-teal-600 text-teal-700 disabled:opacity-40"
                      data-testid="btn-reveal-answer"
                    >
                      {exerciseAnswer.trim().length < 10
                        ? 'Redigez votre réponse (min. 10 caracteres)'
                        : 'Voir le corrige'}
                    </Button>
                  ) : (
                    <div className="mt-3 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                      <p className="text-xs font-semibold text-teal-600 uppercase mb-2">Corrige</p>
                      <p className="text-sm text-teal-800 whitespace-pre-wrap">{currentSlideData.exercise_data.expected_answer}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Audio player */}
        {currentSlideData.has_audio && (
          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-3" data-testid="audio-player">
            <button onClick={onToggleAudio} disabled={audioLoading || !audioUrl} className="w-9 h-9 rounded-full bg-teal-600 text-white flex items-center justify-center hover:bg-teal-700 transition-colors disabled:opacity-50" data-testid="btn-play-audio">
              {audioLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : audioPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            <span className="text-xs text-slate-500">Voix off</span>
            <button onClick={onToggleMute} className="ml-auto text-slate-400 hover:text-slate-600">
              {audioMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            {audioUrl && <audio ref={audioRef} src={audioUrl} onEnded={onAudioEnded} />}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
